import { PlusIcon } from "@xola/icons";
import { ButtonGroup, flash } from "@xola/ui-kit";
import { AxiosResponse } from "axios";
import clsx from "clsx";
import { formatDate, isActiveDate } from "common";
import { concat, get } from "lodash";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { useParams } from "react-router-dom";
import {
    AllowedPrivacy,
    BaseScheduleRepeat,
    BaseScheduleType,
    CutoffType,
    Experience,
    LineItemTemplate,
    PurchaseRule,
    PurchaseRuleLink,
    PurchaseRuleLinkTag,
    Schedule,
    ScheduleDeparture,
    ScheduleType,
} from "types";
import { NoTranslate } from "../../Common/NoTranslate";
import { PromptModal } from "../../Common/PromptModal";
import { ScheduleTable } from "../../Common/ScheduleTable";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { EarlyCutoffWarningModal } from "./EarlyCutoffWarningModal";
import { ScheduleOptionChangeChoice, ScheduleOptionChangeModal } from "./ScheduleOptionChangeModal";
import { api } from "@/api/api";
import { useCollection } from "@/hooks/useCollection";
import { useConfirmationModal } from "@/hooks/useConfirmationModal";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useModal, useVisibility } from "@/hooks/useVisibility";
import { sanitizeExperiencePayload } from "@/models/Experience";
import {
    generatePurchaseRulesFromPriceVariation,
    getSchedulePurchaseRuleLinkPayload,
    unlinkPurchaseRule,
} from "@/models/PriceVariation";
import { getPurchaseRulesFromPurchaseRuleLink } from "@/models/PurchaseRuleLink";
import { getAllowedPrivaciesForAvailableSchedule, haveScheduleOptionsChanged } from "@/models/Schedule";
import { PriceVariationPayload } from "@/types/PriceVariationPayload";

interface ScheduleOptionChangeFields {
    optionsMode?: ScheduleOptionChangeChoice["optionsMode"];
    _emailOverrides?: { sendEmailToTraveler: boolean };
}

type ScheduleSavePayload = Partial<Schedule> & ScheduleOptionChangeFields;

enum ButtonType {
    ACTIVE = 0,
    EXPIRED = 1,
    AUTOMATION_ENGINE = 2,
    ALL = 3,
}

interface filteredSchedulesTypes {
    active: Schedule[];
    expired: Schedule[];
    automationEngine: Schedule[];
}

const filterSchedules = (schedules: Schedule[], tableScheduleType: BaseScheduleType) => {
    const filteredSchedules: filteredSchedulesTypes = {
        active: [],
        expired: [],
        automationEngine: [],
    };

    const activeSchedules = schedules.filter((schedule) => {
        if (schedule.type !== tableScheduleType) {
            return false;
        }

        if (schedule.repeat === BaseScheduleRepeat.WEEKLY) {
            return !schedule.end || isActiveDate(schedule.end);
        }

        return schedule.dates?.some((date) => isActiveDate(date));
    });

    const expiredSchedules = schedules.filter((schedule) => {
        if (schedule.type !== tableScheduleType) {
            return false;
        }

        if (schedule.repeat === BaseScheduleRepeat.WEEKLY) {
            return schedule.end && !isActiveDate(schedule.end);
        }

        return schedule.dates?.every((date) => !isActiveDate(date));
    });

    filteredSchedules.active = activeSchedules;
    filteredSchedules.expired = expiredSchedules;
    filteredSchedules.automationEngine = schedules.filter((schedule) => schedule.name?.includes("(Automation Engine)"));

    return filteredSchedules;
};

const sanatizeDates = (values: Partial<Schedule>) => {
    return {
        ...values,
        start: values.start ? formatDate(values.start) : null,
        end: values.end ? formatDate(values.end) : null,
    };
};

export const SchedulesTable = ({
    experience: initExperience,
    tableScheduleType,
}: {
    experience: Experience;
    tableScheduleType: BaseScheduleType;
}) => {
    const [isCreateScheduleModalOpen, { show: showCreateScheduleModal, toggle: toggleCreateScheduleModal }] =
        useVisibility();
    const [deleteScheduleId, setDeleteScheduleId] = useState<ID | null>(null);
    const { isVisible: showDeleteLastScheduleModal, toggle: toggleDeleteLastScheduleModal } = useModal(false);

    const [activeButton, setActiveButton] = useState(ButtonType.ACTIVE);
    const [isAllDelete, setIsAllDelete] = useState(false);
    const [updatedExperience, setUpdatedExperience] = useState<Experience | null>(null);
    const { id: experienceId } = useParams<{ id?: ID }>();
    const invalidate = useInvalidate();

    const experience = updatedExperience ?? initExperience;

    const { schedules } = experience;
    const availableSchedules = schedules.filter((schedule) => schedule.type === BaseScheduleType.AVAILABLE);
    const schedulesByTableType = schedules.filter((schedule) => schedule.type === tableScheduleType);
    const filteredSchedules = filterSchedules(schedules, tableScheduleType);
    const isAvailableScheduleTable = tableScheduleType === BaseScheduleType.AVAILABLE;

    const { data: schedulePurchaseRuleLinks } = useCollection<PurchaseRuleLink>("purchaseRuleLinks", {
        "tags._id": PurchaseRuleLinkTag.SCHEDULE,
        "entities.id": experienceId,
        expand: "purchaseRule",
        limit: 1,
    });

    const schedulePurchaseRuleLink = schedulePurchaseRuleLinks[0] ?? {};
    const schedulePurchaseRules = getPurchaseRulesFromPurchaseRuleLink(schedulePurchaseRuleLink);

    const { mutateAsync: saveExperience } = useMutation(async (payload: Partial<Experience>) => {
        const { data } = await api.put<Experience>(`/experiences/${experienceId}`, sanitizeExperiencePayload(payload));
        setUpdatedExperience(data);
    });

    const { mutateAsync: createEditSchedule } = useMutation(async (payload: ScheduleSavePayload) => {
        payload = sanatizeDates(payload);
        if (payload.type === BaseScheduleType.AVAILABLE && !payload.id) {
            payload.allowedPrivacies = getAllowedPrivaciesForAvailableSchedule(experience);
        }

        const { data } = await (payload.id
            ? api.put<Schedule>(`/experiences/${experienceId}/schedules/${payload.id}`, payload)
            : api.post<Schedule>(`/experiences/${experienceId}/schedules`, payload));

        const updatedSchedules = payload.id
            ? schedules.map((schedule: Schedule) => (schedule.id === payload.id ? data : schedule))
            : concat(schedules, data);

        const updatedExperience: Experience = { ...experience, schedules: updatedSchedules };

        if (!payload.id && payload.departure) {
            updatedExperience.scheduleType = getScheduleTypeByDeparture(payload.departure);
        }

        setUpdatedExperience(updatedExperience);

        return data;
    });

    const { mutateAsync: updatePurchaseRule } = useMutation(async (payload: Partial<PurchaseRule>) => {
        if (payload?.id) {
            const { id } = payload;
            return api.put<PurchaseRuleLink>(`/purchaseRules/${id}`, payload);
        }

        return api.post(`/purchaseRules`, payload);
    });

    const { mutateAsync: deletePurchaseRule } = useMutation(async (id: ID) => {
        return api.delete(`/purchaseRules/${id}`);
    });

    const { mutateAsync: updatePurchaseRuleLink } = useMutation(async (payload: PurchaseRuleLink) => {
        if (payload?.id) {
            const { id } = payload;
            return api.put<PurchaseRuleLink>(`/purchaseRuleLinks/${id}`, payload);
        }

        return api.post<PurchaseRuleLink>(`/purchaseRuleLinks`, payload);
    });

    const { ConfirmationModal: EarlyCutoffConfirmationModal, confirm: confirmEarlyCutoff } =
        useConfirmationModal(EarlyCutoffWarningModal);

    const { ConfirmationModal: ScheduleOptionChangeConfirmationModal, confirm: confirmScheduleOptionChange } =
        useConfirmationModal<ScheduleOptionChangeChoice | false>(ScheduleOptionChangeModal);

    const { data: lineItemTemplates } = useCollection<LineItemTemplate>("lineItemTemplates");

    const savePurchaseRules = async (updatedPurchaseRules: PurchaseRule[], purchaseRuleLink: PurchaseRuleLink) => {
        const purchaseRulePromises: any = [];

        for (const purchaseRule of updatedPurchaseRules) {
            purchaseRulePromises.push(updatePurchaseRule(purchaseRule));
        }

        await Promise.all(purchaseRulePromises).then(async (result) => {
            const savedPurchaseRules: PurchaseRule[] = result.map((item) => (item as AxiosResponse<PurchaseRule>).data);
            const purchaseRules = getPurchaseRulesFromPurchaseRuleLink(purchaseRuleLink);

            for (const purchaseRule of savedPurchaseRules) {
                if (purchaseRules.length > 0) {
                    const isNewPurchaseRule = purchaseRules.some((rule) => purchaseRule.id !== rule.id);
                    if (isNewPurchaseRule) {
                        purchaseRules.push(purchaseRule);
                    }
                } else {
                    purchaseRules.push(purchaseRule);
                }
            }

            const { data: updatedPurchaseRuleLink } = await updatePurchaseRuleLink(
                getSchedulePurchaseRuleLinkPayload(experience.seller.id, experience, purchaseRuleLink, purchaseRules),
            );
            invalidate("purchaseRuleLinks", updatedPurchaseRuleLink.id);
        });
    };

    const invalidateCollections = () => {
        invalidate("events");
        invalidate("timeslots");
        invalidate("experiences", experienceId);
        invalidate("products", experienceId);
    };

    const updateExperience = async (departure?: ScheduleDeparture, overrides?: Partial<Experience>) => {
        if (!experience) {
            return;
        }

        const newScheduleType = departure ? getScheduleTypeByDeparture(departure) : undefined;

        // Here experience object has the updated value but its not the saved one
        // If the experience schedule is turned on and not saved, need to update experience schedule Type before creating schedule
        if (
            overrides ||
            experience.scheduleType !== newScheduleType ||
            (experience.scheduleType === newScheduleType && experience.schedules.length === 0)
        ) {
            const allowedPrivacies = experience.allowedPrivacies
                ? [...experience.allowedPrivacies]
                : [AllowedPrivacy.PUBLIC];
            return saveExperience({ ...experience, scheduleType: newScheduleType, allowedPrivacies, ...overrides });
        }
    };

    const handleScheduleCreate = async (values: Schedule, priceVariationData: PriceVariationPayload) => {
        const isCreatingTimedSchedule =
            tableScheduleType === BaseScheduleType.AVAILABLE &&
            !!values.departure &&
            getScheduleTypeByDeparture(values.departure) === ScheduleType.TIME &&
            initExperience.cutoffType === CutoffType.PRE_OPENING_END;

        if (isCreatingTimedSchedule) {
            const confirmed = await confirmEarlyCutoff();
            if (!confirmed) {
                return;
            }
        }

        // update schedule type before create schedules
        const overrides = isCreatingTimedSchedule ? { cutoffType: CutoffType.PRE_START } : undefined;

        await updateExperience(values.departure, overrides);

        try {
            const schedule = await createEditSchedule(values);

            if (priceVariationData) {
                const { priceVariation, purchaseRuleLink } = priceVariationData;
                const purchaseRules = generatePurchaseRulesFromPriceVariation(
                    schedule.id!,
                    experience,
                    lineItemTemplates,
                    priceVariation,
                    purchaseRuleLink,
                );
                await savePurchaseRules(purchaseRules, purchaseRuleLink);
            }

            invalidateCollections();
            flash.success("Successfully created schedule");
        } catch (error: any) {
            const errorStatus = get(error, "response.status");
            const reason = get(error, "response.data.global");

            if (errorStatus === 409 && reason[0] === "name_exists") {
                flash.danger(`Schedule with name ${values.name} already exists`);
            }

            await updateExperience(getDepartureByScheduleType(experience?.scheduleType));
        }
    };

    const handleBeforeScheduleEdit = async (values: Schedule): Promise<Schedule | false> => {
        const savedSchedule = schedules.find((schedule) => schedule.id === values.id);
        if (!haveScheduleOptionsChanged(savedSchedule?.options, values.options)) {
            return values;
        }

        const choice = await confirmScheduleOptionChange();
        if (!choice) {
            return false;
        }
        const payload: Schedule & ScheduleOptionChangeFields = {
            ...values,
            optionsMode: choice.optionsMode,
            _emailOverrides: { sendEmailToTraveler: choice.shouldNotifyCustomer },
        };
        return payload;
    };

    const handleScheduleEdit = async (values: Schedule, priceVariationData: PriceVariationPayload) => {
        await createEditSchedule(values);
        if (priceVariationData) {
            const { priceVariation, purchaseRuleLink } = priceVariationData;
            const scheduleId = values.id ?? "";
            const purchaseRules = generatePurchaseRulesFromPriceVariation(
                scheduleId,
                experience,
                lineItemTemplates,
                priceVariation,
                purchaseRuleLink,
            );
            await savePurchaseRules(purchaseRules, purchaseRuleLink);
        }

        invalidateCollections();
        flash.success("Successfully updated schedule");
    };

    const { mutateAsync: deleteSchedule, isLoading } = useMutation(async (id: ID) => {
        await api.delete(`/experiences/${experienceId}/schedules/${id}`);
        const updatedSchedules = schedules.filter((schedule) => schedule.id !== id);
        setUpdatedExperience({ ...experience, schedules: updatedSchedules });
    });

    const updatePurchaseRules = async (scheduleId: ID, scheduleIds?: ID[]) => {
        const purchaseRulePromises: any = [];

        for (let purchaseRule of schedulePurchaseRules) {
            if (
                purchaseRule.meta?.schedules?.includes(scheduleId) ||
                (scheduleIds && purchaseRule.meta?.schedules?.some((id) => scheduleIds.includes(id)))
            ) {
                purchaseRule = unlinkPurchaseRule(purchaseRule, scheduleId, scheduleIds);
                if (purchaseRule.id && purchaseRule.meta?.schedules?.length === 0) {
                    purchaseRulePromises.push(deletePurchaseRule(purchaseRule.id));
                } else {
                    purchaseRulePromises.push(updatePurchaseRule(purchaseRule));
                }
            }
        }

        await Promise.all(purchaseRulePromises).then(async (result) => {
            const savedPurchaseRules: PurchaseRule[] = result.map((item) => (item as AxiosResponse<PurchaseRule>).data);
            const { data: updatedPurchaseRuleLink } = await updatePurchaseRuleLink(
                getSchedulePurchaseRuleLinkPayload(
                    experience.seller.id,
                    experience,
                    schedulePurchaseRuleLink,
                    savedPurchaseRules,
                ),
            );
            invalidate("purchaseRuleLinks", updatedPurchaseRuleLink.id);
        });
    };

    const handleDelete = async () => {
        const isDeletingAvailableSchedule = availableSchedules.some((schedule) => schedule.id === deleteScheduleId);
        if (availableSchedules.length <= 1 && isDeletingAvailableSchedule) {
            // Showing message when deleting the last available schedule
            toggleDeleteLastScheduleModal();
        } else {
            handleConfirmDelete();
        }
    };

    const handleButtonChange = (type: ButtonType) => {
        setActiveButton(type);
    };

    const getVisibleSchedules = (type: ButtonType) => {
        switch (type) {
            case ButtonType.ACTIVE:
                return filteredSchedules.active;
            case ButtonType.EXPIRED:
                return filteredSchedules.expired;
            case ButtonType.ALL:
                return schedules;
            case ButtonType.AUTOMATION_ENGINE:
                return filteredSchedules.automationEngine;
            // no default
        }
    };

    const visibleSchedules = getVisibleSchedules(activeButton).filter(
        (schedule) => schedule.type === tableScheduleType,
    );

    const handleConfirmDelete = async () => {
        if (deleteScheduleId) {
            if (isAllDelete) {
                const ids = [];
                for (const schedule of visibleSchedules) {
                    if (schedule.id) {
                        await deleteSchedule(schedule.id);
                        ids.push(schedule.id);
                    }
                }

                updatePurchaseRules("", ids);
            } else {
                await deleteSchedule(deleteScheduleId);
                updatePurchaseRules(deleteScheduleId);
            }

            invalidateCollections();
            setDeleteScheduleId(null);
            setIsAllDelete(false);
            if (showDeleteLastScheduleModal) {
                toggleDeleteLastScheduleModal();
            }

            flash.success("Successfully deleted schedule ");
        }
    };

    return (
        <div className="flex flex-col space-y-3">
            <EarlyCutoffConfirmationModal />
            <ScheduleOptionChangeConfirmationModal />
            <span className="text-sm font-bold">
                {isAvailableScheduleTable ? "Available Schedules" : "Blackout Schedules"}
            </span>
            <div className="flex flex-wrap items-center justify-between gap-2">
                <ButtonGroup size="small" value={activeButton} onChange={handleButtonChange}>
                    <ButtonGroup.Button className={clsx(filteredSchedules.active.length > 0 ? "visible" : "hidden")}>
                        Active <NoTranslate>({filteredSchedules.active.length})</NoTranslate>
                    </ButtonGroup.Button>
                    <ButtonGroup.Button className={clsx(filteredSchedules.expired.length > 0 ? "visible" : "hidden")}>
                        Expired <NoTranslate>({filteredSchedules.expired.length})</NoTranslate>
                    </ButtonGroup.Button>
                    <ButtonGroup.Button
                        className={clsx(filteredSchedules.automationEngine.length > 0 ? "visible" : "hidden")}
                    >
                        Automation Engine <NoTranslate>({filteredSchedules.automationEngine.length})</NoTranslate>
                    </ButtonGroup.Button>
                    <ButtonGroup.Button className={clsx(schedulesByTableType.length > 0 ? "visible" : "hidden")}>
                        All <NoTranslate>({schedulesByTableType.length})</NoTranslate>
                    </ButtonGroup.Button>
                </ButtonGroup>

                <SecondaryButton size="small" icon={<PlusIcon />} onClick={showCreateScheduleModal}>
                    {isAvailableScheduleTable ? "Add Available Schedule" : "Add Blackout Schedule"}
                </SecondaryButton>
            </div>

            <ScheduleTable
                schedules={visibleSchedules}
                experience={experience}
                blackoutSchedules={!isAvailableScheduleTable}
                showCreateModal={isCreateScheduleModalOpen}
                activeButton={activeButton}
                setIsAllDelete={(isAll) => setIsAllDelete(isAll)}
                onCreateModalVisibilityToggle={toggleCreateScheduleModal}
                onCreate={handleScheduleCreate}
                onEdit={handleScheduleEdit}
                onBeforeEdit={handleBeforeScheduleEdit}
                onDelete={(scheduleId: ID) => setDeleteScheduleId(scheduleId)}
            />

            <PromptModal
                title="Delete Schedule?"
                isLoading={isLoading}
                isOpen={!!deleteScheduleId && !showDeleteLastScheduleModal}
                onConfirm={handleDelete}
                onClose={() => setDeleteScheduleId(null)}
            >
                <div className="flex flex-col space-y-2">
                    <span>
                        You are about to modify a schedule that may have bookings associated with it. Completing this
                        action may result in the following side-effects:
                    </span>
                    <ul>
                        <li className="font-bold"> - Reduction in availability on partially booked trips</li>
                        <li className="font-bold"> - Formerly opened trips becoming unavailable to book</li>
                    </ul>
                </div>
            </PromptModal>

            <PromptModal
                title="Delete Schedule?"
                isLoading={isLoading}
                isOpen={!!showDeleteLastScheduleModal}
                onConfirm={handleConfirmDelete}
                onClose={() => {
                    setDeleteScheduleId(null);
                    toggleDeleteLastScheduleModal();
                    setIsAllDelete(false);
                }}
            >
                <div className="flex flex-col space-y-2">
                    <span>
                        By performing this action, the experience will no longer have schedules, making it an all-day
                        experience with daily availability.
                    </span>
                </div>
            </PromptModal>
        </div>
    );
};

const getScheduleTypeByDeparture = (departure: ScheduleDeparture): ScheduleType => {
    return departure === ScheduleDeparture.FIXED ? ScheduleType.TIME : ScheduleType.DAY;
};

const getDepartureByScheduleType = (scheduleType: ScheduleType = ScheduleType.TIME): ScheduleDeparture => {
    return scheduleType === ScheduleType.TIME ? ScheduleDeparture.FIXED : ScheduleDeparture.VARIES;
};
